'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { Banner } from '@statura/ui/src/components/detail-page/banner';

import type { ProviderProgramContext } from '@/lib/navigation/provider-type';
import { ProgramLensActionButton } from './program-lens-action-button';

export function ProgramLensSelectionPanel({
  programContext,
  returnTo,
  unselected = false,
}: {
  programContext: ProviderProgramContext;
  returnTo: string;
  unselected?: boolean;
}) {
  const [pendingFamily, setPendingFamily] = useState<string | null>(null);
  const [switchFailed, setSwitchFailed] = useState(false);
  return (
    <div
      className="w-full min-w-0 space-y-2"
      role="group"
      aria-label="Choose a programme workspace"
      aria-busy={pendingFamily !== null || undefined}
    >
      {programContext.selectionInvalid ? (
        <Banner tone="warning">
          Your programme selection has changed. Choose a programme again.
        </Banner>
      ) : null}
      {switchFailed ? (
        <Banner tone="danger" role="alert" className="text-text-body">
          Couldn't switch programme. Try again.
        </Banner>
      ) : null}
      <p role="status" className="sr-only">
        {pendingFamily
          ? `Switching to ${programContext.lensOptions.find((option) => option.family === pendingFamily)?.label}…`
          : ''}
      </p>
      <div className="grid gap-2 lg:grid-cols-2">
        {programContext.lensOptions.map((option) => (
          <ProgramLensActionButton
            key={option.family}
            returnTo={returnTo}
            programFamily={option.family}
            disabled={pendingFamily !== null}
            pendingLabel={`Switching to ${option.label}…`}
            onPendingChange={(pending) => {
              setPendingFamily(pending ? option.family : null);
              if (pending) setSwitchFailed(false);
            }}
            onSwitchError={() => setSwitchFailed(true)}
            className="flex w-full items-center justify-between gap-3 rounded-control border border-control-border/30 bg-surface-sheet px-3 py-2.5 text-left text-body font-medium text-text-body hover:border-statura-green-accessible/40 hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-wait disabled:opacity-70 whitespace-normal"
          >
            <span>{option.label}</span>
          </ProgramLensActionButton>
        ))}
      </div>
      {!unselected && !programContext.selectionRequired ? (
        <p className="inline-flex items-center gap-1 text-caption text-text-muted">
          <Check className="h-3.5 w-3.5" aria-hidden="true" /> Current selection remains valid.
        </p>
      ) : null}
    </div>
  );
}
